import React, { useState } from 'react';
import { PasskeySetup } from './PasskeySetup';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { User } from '../App';
import { toast } from 'sonner';
import { 
  User as UserIcon, 
  Shield, 
  Bell, 
  Fingerprint, 
  Moon, 
  Download, 
  Cloud, 
  HelpCircle, 
  Phone, 
  LogOut,
  Copy,
  ChevronRight,
  Settings as SettingsIcon,
  Key,
  Calendar,
  Globe,
  Check,
  Plus,
  Sun,
  Database,
  MessageCircle,
  Lock,
  Smartphone,
  Languages,
  Eye,
  EyeOff,
  X,
  Trash2,
  Upload,
  RefreshCw,
  AlertTriangle,
  Info
} from 'lucide-react';
import { i18n, Language } from '../utils/i18n';
import { getBrandName } from '../utils/brand';

interface SettingsProps {
  user: User;
  onLogout: () => void;
  darkMode: boolean;
  onToggleDarkMode: () => void;
  isAdmin: boolean;
  onAdminLogin: (password: string) => boolean;
  eventSettings: {
    startDate: string;
    endDate: string;
  };
  onEventSettingsUpdate: (settings: { startDate: string; endDate: string }) => void;
}

export function Settings({ user, onLogout, darkMode, onToggleDarkMode, isAdmin, onAdminLogin, eventSettings, onEventSettingsUpdate }: SettingsProps) {
  const [notifications, setNotifications] = useState(true);
  const [biometrics, setBiometrics] = useState(true); // 기본값을 true로 변경하여 모든 사용자가 사용 가능하게
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [showPasskey, setShowPasskey] = useState(false);
  const [passkeyOn, setPasskeyOn] = useState(false);
  React.useEffect(() => {
    (async () => {
      try {
        const t = localStorage.getItem('handy-jwt-token');
        if (!t) return;
        const j = await fetch('/api/passkey/list', { headers: { Authorization: `Bearer ${t}` } }).then(r => r.json());
        if (j?.success) setPasskeyOn((j.passkeys || j.items || []).length > 0);
      } catch { /* 조회 실패는 화면을 막지 않는다 */ }
    })();
  }, [showPasskey]);
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [showPinChangeModal, setShowPinChangeModal] = useState(false);
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  const [newPin, setNewPin] = useState('');
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [newPhone, setNewPhone] = useState('');
  const [phoneSaving, setPhoneSaving] = useState(false);
  const [confirmPin, setConfirmPin] = useState('');
  const [localEventSettings, setLocalEventSettings] = useState(eventSettings);
  const [currentLanguage, setCurrentLanguage] = useState(i18n.getCurrentLanguage());

  const handleExportData = () => {
    const userData = {
      nickname: user.nickname,
      walletAddress: user.walletAddress,
      balance: user.balance,
      exportDate: new Date().toISOString()
    };

    const dataStr = JSON.stringify(userData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `handy-wallet-${user.nickname}-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    
    URL.revokeObjectURL(url);
    toast.success('Data exported successfully!');
  };

  const handleChangePhone = async () => {
    if (!newPhone.trim()) {
      toast.error('전화번호를 입력해주세요.');
      return;
    }
    setPhoneSaving(true);
    try {
      const token = localStorage.getItem('handy-jwt-token');
      const API_URL = import.meta.env.VITE_API_URL || '';
      const res = await fetch(`${API_URL}/api/vatton/users/change-phone`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ phoneNumber: newPhone })
      });
      const data = await res.json();
      if (data.success) {
        toast.success('전화번호가 변경되었습니다.');
        setShowPhoneModal(false);
        setNewPhone('');
      } else {
        toast.error(data.error || '전화번호 변경에 실패했습니다.');
      }
    } catch {
      toast.error('서버 연결에 실패했습니다.');
    } finally {
      setPhoneSaving(false);
    }
  };

  const handleChangePin = () => {
    setShowPinChangeModal(true);
  };

  const handlePinChangeSubmit = async () => {
    if (!newPin || !confirmPin) {
      toast.error('Please enter both PIN fields.');
      return;
    }
    
    if (newPin.length < 4 || newPin.length > 6) {
      toast.error('PIN must be 4-6 digits.');
      return;
    }

    if (newPin !== confirmPin) {
      toast.error('PINs do not match.');
      return;
    }

    try {
      const token = localStorage.getItem('handy-jwt-token');
      if (!token) {
        console.error('인증 토큰이 없습니다.');
        toast.error(i18n.t('authRequired'));
        return;
      }

      const API_URL = import.meta.env.VITE_API_URL || '';
      const response = await fetch(`${API_URL}/api/vatton/users/change-pin`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          newPin: newPin
        })
      });

      if (!response.ok) {
        throw new Error('PIN 변경 실패');
      }

      const result = await response.json();

      setShowPinChangeModal(false);
      setNewPin('');
      setConfirmPin('');
      toast.success('비밀번호가 변경되었습니다.');
      console.log('PIN 변경 성공:', result);
    } catch (error) {
      console.error('PIN 변경 오류:', error);
      toast.error(i18n.t('pinChangeFailed'));
    }
  };

  const handleLogoutClick = () => {
    if (confirm('Are you sure you want to logout?')) {
      onLogout();
    }
  };

  const handleAdminLoginAttempt = () => {
    if (onAdminLogin(adminPassword)) {
      setShowAdminLogin(false);
      setAdminPassword('');
      toast.success('Admin access granted!');
    } else {
      toast.error('Invalid admin password');
      setAdminPassword('');
    }
  };

  const handleEventSettingsSave = () => {
    onEventSettingsUpdate(localEventSettings);
    toast.success('Event settings updated successfully!');
  };

  const copyWalletAddress = () => {
    if (user?.walletAddress) {
      navigator.clipboard.writeText(user.walletAddress);
      toast.success('Wallet address copied!');
    } else {
      toast.error('Wallet address not available');
    }
  };

  const handleLanguageChange = (language: Language) => {
    setCurrentLanguage(language);
    i18n.setLanguage(language);
    setShowLanguageModal(false);
    toast.success(`언어를 ${getLanguageName(language)} 로 바꿨습니다`);
    window.location.reload();
  };

  const getLanguageName = (lang: Language) => {
    switch (lang) {
      case 'en': return 'English';
      case 'ko': return '한국어';
      case 'id': return 'Bahasa Indonesia';
      default: return 'English';
    }
  };

  const handleBiometricsToggle = async (enabled: boolean) => {
    if (enabled) {
      try {
        if ('credentials' in navigator) {
          const publicKeyCredentialRequestOptions = {
            challenge: new Uint8Array(32),
            timeout: 60000,
            userVerification: 'required' as const
          };
          
          try {
            await navigator.credentials.get({
              publicKey: publicKeyCredentialRequestOptions
            });
            setBiometrics(true);
            toast.success('Biometric authentication enabled!');
          } catch (error) {
            if (confirm('Enable biometric authentication? (This is a demo - actual biometric verification would occur here)')) {
              setBiometrics(true);
              toast.success('Biometric authentication enabled!');
            }
          }
        } else {
          if (confirm('Enable biometric authentication? (This is a demo - actual biometric verification would occur here)')) {
            setBiometrics(true);
            toast.success('Biometric authentication enabled!');
          }
        }
      } catch (error) {
        toast.error('Failed to enable biometric authentication');
      }
    } else {
      setBiometrics(false);
      toast.success('Biometric authentication disabled');
    }
  };

  return (
    <>
    <div className="h-full bg-background flex flex-col">
      <div className="flex-1 overflow-auto scrollbar-hide p-4 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between pt-2">
          <div>
            <h1 className="text-2xl font-light text-foreground">설정</h1>
            <p className="text-muted-foreground">계정과 앱 설정을 관리합니다</p>
          </div>
          <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center">
            <SettingsIcon className="h-5 w-5 text-muted-foreground" />
          </div>
        </div>

        {/* Profile Card */}
        <Card className="bg-gradient-to-br from-blue-600 to-blue-500 border-0 text-white">
          <CardContent className="p-6">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                <span className="text-2xl font-medium">
                  {user?.nickname?.charAt(0).toUpperCase() || 'U'}
                </span>
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-medium">{user?.nickname || '회원'}</h3>
                <p className="text-white/80 text-sm">{getBrandName()} 지갑 회원</p>
                <div className="mt-2">
                  <span className="bg-white/20 text-white text-xs px-2 py-1 rounded-full">
                    {(user?.balance || 0).toFixed(2)} wHANDY
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Account Section */}
        <div className="space-y-3">
          <h3 className="text-muted-foreground font-medium">계정</h3>
          
          <Card className="bg-card border-border">
            <CardContent className="p-0">
              <div className="p-4 border-b border-border">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <UserIcon className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-foreground font-medium">닉네임</p>
                      <p className="text-muted-foreground text-sm">{user?.nickname || '회원'}</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="p-4 border-b border-border">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Shield className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-foreground font-medium">지갑 주소</p>
                      <p className="text-muted-foreground text-sm font-mono">
                        {user.walletAddress.slice(0, 10)}...{user.walletAddress.slice(-8)}
                      </p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={copyWalletAddress}
                    className="text-muted-foreground hover:text-foreground p-1 h-auto"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <UserIcon className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-foreground font-medium">가입일</p>
                      <p className="text-muted-foreground text-sm">
                        {new Date(user.createdAt).toLocaleDateString('ko-KR', {
                          year: 'numeric',
                          month: 'long'
                        })}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Security Section */}
        <div className="space-y-3">
          <h3 className="text-muted-foreground font-medium">보안</h3>
          
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Shield className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-foreground font-medium">비밀번호 변경</p>
                    <p className="text-muted-foreground text-sm">지갑 비밀번호를 변경합니다</p>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleChangePin}
                  className="text-muted-foreground hover:text-foreground p-1 h-auto"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* 전화번호 변경 */}
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Shield className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-foreground font-medium">전화번호 변경</p>
                    <p className="text-muted-foreground text-sm">등록된 전화번호를 변경합니다</p>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setShowPhoneModal(true)}
                  className="text-muted-foreground hover:text-foreground p-1 h-auto"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>

        </div>

        {/* Preferences Section */}
        <div className="space-y-3">
          <h3 className="text-muted-foreground font-medium">환경 설정</h3>
          
          <Card className="bg-card border-border">
            <CardContent className="p-0">
              <div className="p-4 border-b border-border">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Bell className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-foreground font-medium">푸시 알림</p>
                      <p className="text-muted-foreground text-sm">입출금·보상 알림 (아직 준비 중입니다)</p>
                    </div>
                  </div>
                  <Switch
                    checked={false}
                    disabled
                  />
                </div>
              </div>
              
              <div className="p-4 border-b border-border">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Fingerprint className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-foreground font-medium">지문·얼굴로 로그인</p>
                      <p className="text-muted-foreground text-sm">아직 준비 중입니다</p>
                    </div>
                  </div>
                  {/* 지문 등록 — 정책 확정 전까지 숨김 (2026-10-15).
                      켜려면 아래 Switch 를 지우고 아래 주석의 Button 을 되살리면 된다.
                  <Button size="sm" variant={passkeyOn ? 'ghost' : 'default'}
                    onClick={() => setShowPasskey(true)} disabled={passkeyOn}>
                    {passkeyOn ? '등록됨' : '등록하기'}
                  </Button> */}
                  <Switch checked={false} disabled />
                </div>
              </div>
              
              <div className="p-4 border-b border-border">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Globe className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-foreground font-medium">언어</p>
                      <p className="text-muted-foreground text-sm">{getLanguageName(currentLanguage)}</p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setShowLanguageModal(true)}
                    className="text-muted-foreground hover:text-foreground p-1 h-auto"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Moon className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-foreground font-medium">어두운 화면</p>
                      <p className="text-muted-foreground text-sm">밝은 화면과 어두운 화면을 바꿉니다</p>
                    </div>
                  </div>
                  <Switch
                    checked={darkMode}
                    onCheckedChange={onToggleDarkMode}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Data Management */}
        <div className="space-y-3">
          <h3 className="text-muted-foreground font-medium">Data</h3>
          
          <Card className="bg-card border-border">
            <CardContent className="p-0">
              <div className="p-4 border-b border-border">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Download className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-foreground font-medium">Export Data</p>
                      <p className="text-muted-foreground text-sm">Download your wallet data</p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled
                    className="text-muted-foreground/50 p-1 h-auto cursor-not-allowed"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Cloud className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-foreground font-medium">Cloud Sync</p>
                      <p className="text-muted-foreground text-sm">Backup to cloud storage</p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled
                    className="text-muted-foreground/50 p-1 h-auto cursor-not-allowed"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Server Monitoring Section */}
        <div className="space-y-3">
          <h3 className="text-muted-foreground font-medium">{i18n.t('serverMonitoring')}</h3>
          
          <Card className="bg-card border-border">
            <CardContent className="p-0">
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <SettingsIcon className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-foreground font-medium">Server Status</p>
                      <p className="text-muted-foreground text-sm">Monitor server performance and health</p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled
                    className="text-muted-foreground p-1 h-auto cursor-not-allowed opacity-50"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>


        {/* Support Section */}
        <div className="space-y-3">
          <h3 className="text-muted-foreground font-medium">Support</h3>
          
          <Card className="bg-card border-border">
            <CardContent className="p-0">
              <div className="p-4 border-b border-border">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <HelpCircle className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-foreground font-medium">Help & FAQ</p>
                      <p className="text-muted-foreground text-sm">Get answers to common questions</p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => toast.info('Opening help center.')}
                    className="text-muted-foreground hover:text-foreground p-1 h-auto"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Phone className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-foreground font-medium">Contact Support</p>
                      <p className="text-muted-foreground text-sm">Get help from our team</p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => toast.info('Contacting support team.')}
                    className="text-muted-foreground hover:text-foreground p-1 h-auto"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Logout */}
        <Card className="bg-destructive/10 border-destructive/20">
          <CardContent className="p-4">
            <Button
              onClick={handleLogoutClick}
              className="w-full h-12 bg-destructive hover:bg-destructive/90 text-destructive-foreground font-medium rounded-xl flex items-center justify-center gap-3"
            >
              <LogOut className="h-5 w-5" />
              Sign Out
            </Button>
          </CardContent>
        </Card>

        {/* App Version */}
        <div className="text-center py-4">
          <p className="text-muted-foreground text-sm">{getBrandName()} Wallet v1.0.0</p>
          <p className="text-muted-foreground/60 text-xs mt-1">© 2025 {getBrandName()}. All rights reserved.</p>
        </div>
      </div>

      {/* Admin Login Modal */}
      {showAdminLogin && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-2xl p-6 w-full max-w-sm shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-foreground mb-2">Admin Access</h3>
              <p className="text-muted-foreground text-sm">Enter admin password to continue</p>
            </div>

            <div className="space-y-4">
              <div>
                <input
                  type="password"
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  placeholder="Admin Password"
                  className="w-full px-4 py-3 border border-border rounded-xl bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleAdminLoginAttempt();
                    }
                  }}
                />
              </div>
              
              <div className="flex space-x-3">
                <Button
                  onClick={() => {
                    setShowAdminLogin(false);
                    setAdminPassword('');
                  }}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleAdminLoginAttempt}
                  disabled={!adminPassword}
                  className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
                >
                  Login
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Language Selection Modal */}
      {showLanguageModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-2xl p-6 w-full max-w-sm shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Globe className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-foreground mb-2">Select Language</h3>
              <p className="text-muted-foreground text-sm">Choose your preferred language</p>
            </div>

            <div className="space-y-3">
              {(['en', 'ko', 'id'] as Language[]).map((lang) => (
                <button
                  key={lang}
                  onClick={() => handleLanguageChange(lang)}
                  className={`w-full flex items-center justify-between p-4 rounded-xl border transition-colors ${
                    currentLanguage === lang
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border bg-background text-foreground hover:bg-accent'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className="text-2xl">
                      {lang === 'en' && '🇺🇸'}
                      {lang === 'ko' && '🇰🇷'}
                      {lang === 'id' && '🇮🇩'}
                    </div>
                    <div className="text-left">
                      <p className="font-medium">{getLanguageName(lang)}</p>
                      <p className="text-sm opacity-60">
                        {lang === 'en' && 'English'}
                        {lang === 'ko' && '한국어'}
                        {lang === 'id' && 'Bahasa Indonesia'}
                      </p>
                    </div>
                  </div>
                  {currentLanguage === lang && (
                    <Check className="h-5 w-5 text-primary" />
                  )}
                </button>
              ))}
            </div>

            <Button
              onClick={() => setShowLanguageModal(false)}
              variant="outline"
              className="w-full mt-4 border-border text-muted-foreground hover:bg-accent"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* PIN Change Modal */}
      {showPhoneModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-2xl p-6 w-full max-w-sm shadow-2xl">
            <h3 className="font-bold text-lg text-foreground mb-2">전화번호 변경</h3>
            <p className="text-muted-foreground text-sm mb-4">변경할 전화번호를 입력해주세요.</p>
            <input
              type="tel"
              value={newPhone}
              onChange={(e) => setNewPhone(e.target.value)}
              placeholder="010-0000-0000"
              className="w-full h-12 px-3 rounded-xl border border-border bg-background text-foreground mb-4"
            />
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => { setShowPhoneModal(false); setNewPhone(''); }}
              >
                취소
              </Button>
              <Button
                className="flex-1 text-white"
                style={{ background: '#FF6B00' }}
                onClick={handleChangePhone}
                disabled={phoneSaving}
              >
                {phoneSaving ? '변경 중' : '변경'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {showPinChangeModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-2xl p-6 w-full max-w-sm shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Key className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-foreground mb-2">Change PIN</h3>
              <p className="text-muted-foreground text-sm">Enter your new 4-6 digit PIN</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  New PIN
                </label>
                <input
                  type="password"
                  value={newPin}
                  onChange={(e) => setNewPin(e.target.value)}
                  placeholder="Enter new PIN"
                  maxLength={6}
                  className="w-full px-4 py-3 border border-border rounded-xl bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Confirm PIN
                </label>
                <input
                  type="password"
                  value={confirmPin}
                  onChange={(e) => setConfirmPin(e.target.value)}
                  placeholder="Confirm new PIN"
                  maxLength={6}
                  className="w-full px-4 py-3 border border-border rounded-xl bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handlePinChangeSubmit();
                    }
                  }}
                />
              </div>
              
              <div className="flex space-x-3">
                <Button
                  onClick={() => {
                    setShowPinChangeModal(false);
                    setNewPin('');
                    setConfirmPin('');
                  }}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handlePinChangeSubmit}
                  disabled={!newPin || !confirmPin}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                >
                  Update PIN
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>

      {/* 지문 등록 — 본인 확인 후 이 휴대폰에 묶는다 (2026-10-15) */}
      <PasskeySetup
        open={showPasskey}
        onClose={() => setShowPasskey(false)}
        authHeaders={() => {
          const t = localStorage.getItem('handy-jwt-token');
          return t ? { Authorization: `Bearer ${t}` } : {};
        }}
      />
    </>
  );
}